import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FilePreview from './FilePreview';

const DocumentManager = () => {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingItem, setRenamingItem] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [inlinePreview, setInlinePreview] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    fetchContent();
  }, [currentPath]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`/api/documents?path=${encodeURIComponent(currentPath)}`, { 
        withCredentials: true 
      });
      setDocuments(response.data.documents || []);
      setFolders(response.data.folders || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('path', currentPath);
        formData.append('description', `Uploaded ${new Date().toLocaleDateString()}`);
        
        await axios.post('/api/documents/upload', formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      await fetchContent();
      setError(null);
    } catch (error) {
      console.error('Upload failed:', error);
      setError(`Upload failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await axios.post('/api/folders/create', {
        name: newFolderName.trim(),
        path: currentPath
      }, { withCredentials: true });
      
      await fetchContent();
      setShowNewFolderDialog(false);
      setNewFolderName('');
      setError(null);
    } catch (error) {
      console.error('Create folder failed:', error);
      setError(`Create folder failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleRename = async () => {
    if (!renameValue.trim() || !renamingItem) return;
    
    try {
      const endpoint = renamingItem.type === 'folder' ? '/api/folders/rename' : '/api/documents/rename';
      await axios.put(endpoint, {
        id: renamingItem.id,
        newName: renameValue.trim(),
        path: currentPath
      }, { withCredentials: true });
      
      await fetchContent();
      setRenamingItem(null);
      setRenameValue('');
      setError(null);
    } catch (error) {
      console.error('Rename failed:', error);
      setError(`Rename failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (item, type) => {
    const itemName = type === 'folder' ? item.name : item.fileName;
    if (!window.confirm(`Are you sure you want to delete "${itemName}"?`)) return;
    
    try {
      const endpoint = type === 'folder' ? `/api/folders/${item.id}` : `/api/documents/${item.id}`;
      await axios.delete(endpoint, { 
        withCredentials: true,
        params: { path: currentPath }
      });
      await fetchContent();
      setError(null);
    } catch (error) {
      console.error('Delete failed:', error);
      setError(`Delete failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await axios.get(`/api/documents/${document.id}/download`, {
        withCredentials: true,
        responseType: 'blob',
        params: { path: currentPath }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setError(`Download failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handlePreview = (document) => {
    setPreviewDocument(document);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewDocument(null);
  };

  const handleInlinePreview = async (document) => {
    if (inlinePreview?.id === document.id) {
      // Toggle off if same document
      setInlinePreview(null);
      setPreviewContent(null);
      return;
    }

    setInlinePreview(document);
    setPreviewLoading(true);
    setPreviewError(null);
    
    try {
      const response = await fetch(`/api/documents/${document.id}/preview`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load preview');
      }
      
      // Handle different content types
      if (document.mimeType.startsWith('text/') || 
          document.mimeType === 'application/json' || 
          document.mimeType === 'application/xml') {
        const text = await response.text();
        setPreviewContent({ type: 'text', content: text });
      } else if (document.mimeType.startsWith('image/')) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewContent({ type: 'image', url });
      } else if (document.mimeType === 'application/pdf') {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewContent({ type: 'pdf', url });
      } else {
        // Unsupported file type
        setPreviewContent({ type: 'unsupported' });
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewError(error.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const navigateUp = () => {
    const pathParts = currentPath.split('/').filter(p => p);
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const navigateToPath = (pathIndex) => {
    const pathParts = currentPath.split('/').filter(p => p);
    const newPath = pathParts.slice(0, pathIndex + 1).join('/');
    setCurrentPath(newPath);
  };

  const startRename = (item, type) => {
    setRenamingItem({ ...item, type });
    setRenameValue(type === 'folder' ? item.name : item.fileName);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload([...e.dataTransfer.files]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'xls': case 'xlsx': return '📊';
      case 'ppt': case 'pptx': return '📈';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
      case 'zip': case 'rar': return '🗂️';
      case 'txt': return '📋';
      case 'mp4': case 'mov': case 'avi': return '🎬';
      case 'mp3': case 'wav': return '🎵';
      default: return '📎';
    }
  };

  const renderInlinePreview = () => {
    if (!inlinePreview) return null;

    return (
      <div className="mt-3 border-t border-gray-200 pt-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">
              Preview: {inlinePreview.fileName}
            </h4>
            <button
              onClick={() => {
                setInlinePreview(null);
                setPreviewContent(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {previewLoading && (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-blue-400 border-t-transparent"></div>
                <p className="text-gray-500 text-sm mt-2">Loading preview...</p>
              </div>
            </div>
          )}

          {previewError && (
            <div className="flex items-center justify-center h-32">
              <div className="text-center text-red-600">
                <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-sm">Failed to load preview</p>
                <p className="text-xs text-gray-500 mt-1">{previewError}</p>
              </div>
            </div>
          )}

          {previewContent && !previewLoading && (
            <div className="preview-content">
              {previewContent.type === 'text' && (
                <div className="h-48 overflow-auto bg-white p-3 rounded border">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                    {previewContent.content}
                  </pre>
                </div>
              )}

              {previewContent.type === 'image' && (
                <div className="flex items-center justify-center h-48 bg-white rounded border">
                  <img 
                    src={previewContent.url}
                    alt={inlinePreview.fileName}
                    className="max-w-full max-h-full object-contain rounded"
                  />
                </div>
              )}

              {previewContent.type === 'pdf' && (
                <div className="h-48 bg-white rounded border">
                  <iframe
                    src={previewContent.url}
                    width="100%"
                    height="100%"
                    className="rounded"
                    title={inlinePreview.fileName}
                  />
                </div>
              )}

              {previewContent.type === 'unsupported' && (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center text-gray-500">
                    <span className="text-2xl mb-2 block">{getFileIcon(inlinePreview.fileName)}</span>
                    <p className="text-sm">Preview not available for this file type</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const pathParts = currentPath.split('/').filter(p => p);

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            🗃️ Document Library
          </h2>
          <button
            onClick={() => setShowNewFolderDialog(true)}
            className="px-3 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Folder
          </button>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center space-x-1 text-sm mb-4">
          <button
            onClick={() => setCurrentPath('')}
            className={`px-2 py-1 rounded hover:bg-gray-100 ${currentPath === '' ? 'text-blue-600 font-medium' : 'text-gray-600'}`}
          >
            📁 Root
          </button>
          {pathParts.map((part, index) => (
            <React.Fragment key={index}>
              <span className="text-gray-400">/</span>
              <button
                onClick={() => navigateToPath(index)}
                className={`px-2 py-1 rounded hover:bg-gray-100 ${index === pathParts.length - 1 ? 'text-blue-600 font-medium' : 'text-gray-600'}`}
              >
                {part}
              </button>
            </React.Fragment>
          ))}
        </div>

        <p className="text-sm text-gray-600">
          Upload, store, and organize your team documents
        </p>
      </div>

      {/* Upload Area */}
      <div className="p-6 border-b border-gray-200">
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload([...e.target.files])}
            className="hidden"
            id="file-upload"
            disabled={uploading}
          />
          <label htmlFor="file-upload" className={`cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
            <div className="flex flex-col items-center">
              <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm font-medium text-gray-900">
                {uploading ? 'Uploading...' : 'Drop files here or click to browse'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Max 50MB per file</p>
            </div>
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-400 border-t-transparent"></div>
            <p className="text-gray-500 mt-2">Loading...</p>
          </div>
        ) : (
          <div>
            {/* Back Button */}
            {currentPath && (
              <div className="mb-4">
                <button
                  onClick={navigateUp}
                  className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to parent folder
                </button>
              </div>
            )}

            {folders.length === 0 && documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p>This folder is empty</p>
                <p className="text-sm mt-1">Upload files or create folders to get started</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Folders */}
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-2xl">📁</span>
                      <div className="min-w-0 flex-1">
                        {renamingItem?.id === folder.id && renamingItem?.type === 'folder' ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              className="text-sm font-medium text-gray-900 border border-blue-300 rounded px-2 py-1 flex-1"
                              onKeyPress={(e) => e.key === 'Enter' && handleRename()}
                              onBlur={handleRename}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => navigateToFolder(folder.name)}
                              className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate text-left"
                            >
                              {folder.name}
                            </button>
                            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                              <span>Created {new Date(folder.createdAt).toLocaleDateString()}</span>
                              {folder.itemCount !== undefined && (
                                <>
                                  <span>•</span>
                                  <span>{folder.itemCount} items</span>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startRename(folder, 'folder')}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                        title="Rename"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(folder, 'folder')}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}

                {/* Documents */}
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-2xl">{getFileIcon(doc.fileName)}</span>
                      <div className="min-w-0 flex-1">
                        {renamingItem?.id === doc.id && renamingItem?.type === 'file' ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              className="text-sm font-medium text-gray-900 border border-blue-300 rounded px-2 py-1 flex-1"
                              onKeyPress={(e) => e.key === 'Enter' && handleRename()}
                              onBlur={handleRename}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.fileName}
                            </p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                              <span>{formatFileSize(doc.fileSize)}</span>
                              <span>•</span>
                              <span>Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</span>
                              {doc.uploadedBy && (
                                <>
                                  <span>•</span>
                                  <span>by {doc.uploadedBy}</span>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleInlinePreview(doc)}
                        className={`p-2 ${inlinePreview?.id === doc.id ? 'text-blue-600 bg-blue-50' : 'text-green-600 hover:text-green-800 hover:bg-green-50'} rounded-md`}
                        title="Quick Preview"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handlePreview(doc)}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md"
                        title="Full Preview"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                        title="Download"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => startRename(doc, 'file')}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                        title="Rename"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(doc, 'file')}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Inline Preview Area */}
            {renderInlinePreview()}
          </div>
        )}
      </div>

      {/* New Folder Dialog */}
      {showNewFolderDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              onKeyPress={(e) => e.key === 'Enter' && createFolder()}
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewFolderDialog(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={createFolder}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      <FilePreview
        document={previewDocument}
        isOpen={showPreview}
        onClose={closePreview}
      />
    </div>
  );
};

export default DocumentManager;